'use client';

import {useState, useEffect} from 'react';

import Cookie from '@/lib/cookie';
import {Theme} from '@/types/index';

export type LocalSetting = ReturnType<typeof generateDefaultLocalSetting>;

/** Returns client user agent's preferred theme. */
function systemTheme() {
  return typeof window === 'undefined'
    ? Theme.Light
    : window.matchMedia(`(prefers-color-scheme: ${Theme.Light})`).matches
    ? Theme.Light
    : Theme.Dark;
}

/**
 * Generates the default local settings.
 *
 * The `undefined` and `null` should not be used as value
 * due to incompatibility with {@link updateLocalSetting}.
 */
function generateDefaultLocalSetting() {
  const showWarning = process.env.NEXT_PUBLIC_SHOW_COOKIE_WARNING === 'true';
  return {
    /**
     * Whether the data is stored on the `Web Storage API`.
     *
     * Useful to detect whether the data is generated on the
     * server or coming from the browser. The value is always
     * `true` if it's coming from the browser.
     */
    isStored: false,
    version: process.env.NEXT_PUBLIC_VERSION!,
    theme: systemTheme(),
    cookieConsent: {
      showWarning,
      googleAnalytics: !showWarning
    }
  };
}

/** Merges the default local settings with previous stored versions in-place. */
function updateLocalSetting<T extends {[key: string]: any}>(
  current: T,
  previous: T
) {
  const isObject = (object: any) =>
    typeof object === 'object' && object !== null && !Array.isArray(object);

  for (const [key, value] of Object.entries(current) as ObjectEntries<T>)
    if (typeof value === typeof previous[key])
      current[key] =
        isObject(value) && isObject(previous[key])
          ? updateLocalSetting(value, previous[key])
          : previous[key];

  return current;
}

/**
 * Reads and stores application's settings on the `Web Storage API`.
 * @param name - Name of the storage
 * @param encrypt - Encrypt data to the `Base64` format.
 * @param asCookie - Also store the local settings as a cookie.
 *  The cookie's name would be same as the {@link name} parameter.
 *  For server components to render correctly, settings should be stored
 *  as cookie as well to eventually be returned by client on each request
 *  and be used on the server to read the settings and render accordingly.
 * @param defaultSetting - The default local settings value to use.
 */
export function useLocalSetting(
  name = 'setting',
  encrypt = false,
  asCookie = false,
  defaultSetting = generateDefaultLocalSetting()
) {
  const [localSetting, setLocalSetting] = useState(() => {
    if (typeof window === 'undefined') {
      return defaultSetting;
    }

    const setting = localStorage.getItem(name);
    if (setting) {
      const parsedSetting: LocalSetting = JSON.parse(
        encrypt ? window.atob(setting) : setting // decoding
      );
      if (!parsedSetting.isStored) {
        parsedSetting.isStored = true;
      }
      if (parsedSetting.version !== defaultSetting.version) {
        // Updating stored setting to the new version
        parsedSetting.version = defaultSetting.version;
        updateLocalSetting(defaultSetting, parsedSetting);
        storeSetting(parsedSetting);
      }
      return parsedSetting;
    } else {
      return {...defaultSetting, isStored: true};
    }
  });

  /** Stores the setting on the storage. */
  async function storeSetting(localSetting: Readonly<LocalSetting>) {
    const stringifiedSetting = JSON.stringify(localSetting);
    const data = encrypt ? window.btoa(stringifiedSetting) : stringifiedSetting; // encoding
    localStorage.setItem(name, data);
    if (asCookie) {
      const cookie = new Cookie();
      await cookie.set({
        name,
        value: data,
        expires: Date.now() + cookie.ONE_YEAR_TIMESTAMP
      });
    }
  }

  // Storing setting changes
  useEffect(() => void storeSetting(localSetting), [localSetting]); // eslint-disable-line react-hooks/exhaustive-deps

  return [localSetting, setLocalSetting] as const;
}
